import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { api, type Me } from '../lib/api';
import { ThemeToggle } from './ThemeToggle';

/** The top bar: sidebar toggle, search, and who you are signed in as.
 *
 *  Every control here does something. The templates this layout follows also
 *  carry a bell, a language picker and an apps grid; none has a feature behind it
 *  in this product, so none is here. A button that looks live and is inert
 *  teaches people to distrust the rest of the bar.
 *
 *  The moon is the exception, and it is real: styles.css now carries a full dark
 *  palette rather than the inverted guess it previously refused to ship.
 */

type NavItem = { to: string; label: string; glyph: string };
type NavGroup = { section: string; items: NavItem[] };

export function Topbar({
  me,
  nav,
  collapsed,
  onToggleSidebar,
  onSignedOut,
}: {
  me: Me;
  nav: NavGroup[];
  collapsed: boolean;
  onToggleSidebar: () => void;
  onSignedOut: () => Promise<void>;
}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const searchRef = useRef<HTMLInputElement | null>(null);
  const searchBox = useRef<HTMLDivElement | null>(null);
  const menuBox = useRef<HTMLDivElement | null>(null);

  // ⌘K / Ctrl+K, because the placeholder promises it. A hint that does not work
  // is worse than no hint.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        searchRef.current?.focus();
        setOpen(true);
      }
      if (event.key === 'Escape') {
        setOpen(false);
        setMenuOpen(false);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Close on an outside click. Two refs rather than one document-level flag, so
  // clicking from the search into the menu does not leave both open.
  useEffect(() => {
    function onClick(event: MouseEvent) {
      const target = event.target as Node;
      if (searchBox.current && !searchBox.current.contains(target)) setOpen(false);
      if (menuBox.current && !menuBox.current.contains(target)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const trimmed = query.trim();
  const pages = trimmed
    ? nav
        .flatMap((group) => group.items.map((item) => ({ ...item, section: group.section })))
        .filter((item) => item.label.toLowerCase().includes(trimmed.toLowerCase()))
        .slice(0, 6)
    : [];

  // Looks like an address, so searching the mail log is the likely intent.
  const looksLikeAnAddress = trimmed.includes('@');

  function go(to: string) {
    setOpen(false);
    setQuery('');
    navigate(to);
  }

  return (
    <header className="topbar">
      <button
        type="button"
        className="topbar-icon"
        onClick={onToggleSidebar}
        aria-label={collapsed ? 'Expand the sidebar' : 'Collapse the sidebar'}
        aria-expanded={!collapsed}
      >
        <span aria-hidden="true">☰</span>
      </button>

      <div className="topbar-search" ref={searchBox}>
        <span className="topbar-search-icon" aria-hidden="true">
          ⌕
        </span>
        <input
          ref={searchRef}
          type="text"
          value={query}
          placeholder="Search pages, or an email address…"
          aria-label="Search"
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' || !trimmed) return;
            // Enter takes the first sensible thing rather than doing nothing:
            // a matching page if there is one, otherwise the mail log.
            if (pages.length > 0) go(pages[0].to);
            else go(`/emails?search=${encodeURIComponent(trimmed)}`);
          }}
        />
        <kbd className="topbar-kbd">⌘K</kbd>

        {open && trimmed !== '' && (
          <div className="topbar-results" role="listbox">
            {pages.map((item) => (
              <button
                key={item.to}
                type="button"
                className="topbar-result"
                role="option"
                aria-selected="false"
                onClick={() => go(item.to)}
              >
                <span className="glyph" aria-hidden="true">
                  {item.glyph}
                </span>
                <span>{item.label}</span>
                <span className="topbar-result-hint">{item.section}</span>
              </button>
            ))}

            <button
              type="button"
              className="topbar-result"
              role="option"
              aria-selected="false"
              onClick={() => go(`/emails?search=${encodeURIComponent(trimmed)}`)}
            >
              <span className="glyph" aria-hidden="true">
                ✉
              </span>
              <span>
                {looksLikeAnAddress ? 'Find mail to ' : 'Search the mail log for '}
                <strong>{trimmed}</strong>
              </span>
            </button>

            {pages.length === 0 && !looksLikeAnAddress && (
              <div className="topbar-result-empty">
                No page matches. Enter searches the mail log.
              </div>
            )}
          </div>
        )}
      </div>

      <div className="topbar-right">
        {/* The environment the current credential sees. It was in the sidebar
            footer, and it belongs where somebody looks before they act. */}
        <span className="topbar-env">{me.tenant.plan}</span>

        <ThemeToggle />

        <div className="topbar-user" ref={menuBox}>
          <button
            type="button"
            className="topbar-user-btn"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-expanded={menuOpen}
            aria-haspopup="menu"
          >
            <span className="avatar" aria-hidden="true">
              {initials(me.email)}
            </span>
            <span className="topbar-user-text">
              <span className="topbar-user-name">{me.tenant.name}</span>
              <span className="topbar-user-role">{me.org_role}</span>
            </span>
            <span className="topbar-caret" aria-hidden="true">
              ▾
            </span>
          </button>

          {menuOpen && (
            <div className="topbar-menu" role="menu">
              <div className="topbar-menu-head">
                <div className="topbar-menu-name">{me.email}</div>
                <div className="mono topbar-menu-sub">{me.tenant.sending_domain}</div>
              </div>
              <button
                type="button"
                role="menuitem"
                className="topbar-menu-item"
                onClick={() => {
                  setMenuOpen(false);
                  navigate('/settings');
                }}
              >
                Settings
              </button>
              <button
                type="button"
                role="menuitem"
                className="topbar-menu-item"
                onClick={() => {
                  setMenuOpen(false);
                  navigate('/billing');
                }}
              >
                Billing
              </button>
              <button
                type="button"
                role="menuitem"
                className="topbar-menu-item danger"
                onClick={async () => {
                  await api.auth.logout();
                  await onSignedOut();
                }}
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

/** Initials from an email, for the avatar. Two characters at most: three starts
 *  to look like a word and stops reading as a monogram. */
function initials(email: string): string {
  const local = email.split('@')[0] ?? '';
  const parts = local.split(/[._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return local.slice(0, 2).toUpperCase() || '?';
}
